/* eslint-disable react/require-default-props */
/* eslint-disable import/no-extraneous-dependencies */
/**
 * InputCpfCnpj
 *
 * Aplica máscara de CPF (000.000.000-00) para até 11 dígitos
 * e de CNPJ (00.000.000/0000-00) para 12–14 dígitos.
 *
 * Valida algoritmicamente (dígitos verificadores via módulo 11) no blur,
 * espelhando o helper do backend — barra documentos inválidos antes de
 * disparar a requisição. O resultado também é entregue ao caller via
 * `onBlur(event, validationResult)` para que ele possa gatear submit /
 * marcar erro no form / etc.
 *
 * Props:
 *   value      {string}   — valor controlado (apenas dígitos ou formatado)
 *   onChange   {function} — chamado com o valor FORMATADO a cada mudança
 *   onBlur     {function} — opcional; recebe (event, validationResult)
 *   disabled   {boolean}
 *   name       {string}
 *   placeholder {string}
 *   required   {boolean}  — se true, valor vazio também é considerado inválido
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { validateCpfCnpj } from '../../utils/validateCpfCnpj';

function onlyDigits(str) {
   return String(str ?? '').replace(/\D/g, '');
}

function maskCpf(digits) {
   return digits
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2')
      .substring(0, 14);
}

function maskCnpj(digits) {
   return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
}

function applyMask(raw) {
   const digits = onlyDigits(raw);
   if (digits.length <= 11) return maskCpf(digits);
   return maskCnpj(digits);
}

export default function InputCpfCnpj({
   value = '',
   onChange,
   onBlur,
   disabled = false,
   name = 'customer_doc',
   placeholder = '000.000.000-00',
   required = false,
}) {
   // `touched` é setado no blur e limpo a cada digitação — assim o feedback
   // (erro vermelho OU sucesso verde) só aparece depois que o usuário sai do
   // campo, persiste no refocus, e some no momento em que ele altera qualquer
   // caractere (até o próximo blur).
   const [touched, setTouched] = useState(false);

   const digits = onlyDigits(value);
   const validation = validateCpfCnpj(digits);
   // Campo vazio só é "erro" quando required=true; do contrário, vazio é neutro.
   const showError =
      touched &&
      (required ? !validation.valid : digits.length > 0 && !validation.valid);
   const showSuccess = touched && validation.valid;

   let inputStyle;
   if (showError) inputStyle = { borderColor: 'rgba(211,47,47,0.6)', borderWidth: '2px' };
   else if (showSuccess) inputStyle = { borderColor: 'rgba(46,125,50,0.6)', borderWidth: '2px' };

   function handleChange(e) {
      const next = onlyDigits(e.target.value);
      if (next.length > 14) return;
      // Qualquer edição zera o feedback até o próximo blur.
      if (touched) setTouched(false);
      const masked = applyMask(next);
      onChange({ target: { name, value: masked } });
   }

   function handleBlur(e) {
      setTouched(true);
      if (onBlur) onBlur(e, validation);
   }

   return (
      <>
         <input
            type="text"
            name={name}
            value={applyMask(digits)}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            inputMode="numeric"
            aria-invalid={showError || undefined}
            style={inputStyle}
         />
         {showError && (
            <span
               style={{
                  fontSize: 11,
                  color: '#b71c1c',
                  marginTop: 2,
               }}
            >
               {validation.reason}
            </span>
         )}
      </>
   );
}

InputCpfCnpj.propTypes = {
   value: PropTypes.string,
   onChange: PropTypes.func.isRequired,
   onBlur: PropTypes.func,
   disabled: PropTypes.bool,
   name: PropTypes.string,
   placeholder: PropTypes.string,
   required: PropTypes.bool,
};
